import Webcam from "react-webcam";
import { useGeolocated } from "react-geolocated";
import piexif from "piexifjs";
import { isMobile } from "react-device-detect";

import { useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import AppConfig from "../../../../appConfig";

function GeoTaggedImageField({ displayName, fieldRef, options }) {
  const [fieldError, showFieldError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const webcamRef = useRef(`${fieldRef}_${displayName}`);
  const [imgSrc, setImgSrc] = useState(null);
  const [capturedData, setCapturedData] = useState(null);
  const [cameraUsed, setCameraUsed] = useState(false);

  const videoConstraints = {
    facingMode: cameraUsed ? "user" : { exact: "environment" },
  };

  const [openWebCamDialog, setOpenWebCamDialog] = useState(false);

  // ===== EXISTING FUNCTIONS (UNCHANGED) =====
  async function getGeocodeData(lat, lng) {
    const apiKey = AppConfig.google_map_key;
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
    const response = await fetch(url);
    if (!response.ok) return null;
    const data = await response.json();
    return data.results.length > 0 ? data.results[0].formatted_address : null;
  }

  function getHaversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // async function addWatermark(base64, text) {
  //   const img = await new Promise((resolve, reject) => {
  //     const i = new Image();
  //     i.onload = () => resolve(i);
  //     i.onerror = reject;
  //     i.src = base64;
  //   });

  //   const canvas = document.createElement("canvas");
  //   const ctx = canvas.getContext("2d");
  //   canvas.width = img.width;
  //   canvas.height = img.height;

  //   ctx.drawImage(img, 0, 0);

  //   const fontSize = Math.round(img.width * (isMobile ? 0.05 : 0.03));
  //   const lineHeight = fontSize * 1.3;
  //   ctx.font = `${fontSize}px Arial`;
  //   ctx.textBaseline = "top";

  //   const maxWidth = img.width * 0.9;
  //   const paragraphs = text.split(/\r?\n/);
  //   const wrappedLines = [];

  //   for (const para of paragraphs) {
  //     const words = para.split(" ");
  //     let line = "";

  //     for (const word of words) {
  //       const testLine = line ? `${line} ${word}` : word;
  //       if (ctx.measureText(testLine).width > maxWidth) {
  //         wrappedLines.push(line);
  //         line = word;
  //       } else {
  //         line = testLine;
  //       }
  //     }
  //     if (line) wrappedLines.push(line);
  //   }

  //   const textWidth = Math.max(
  //     ...wrappedLines.map((l) => ctx.measureText(l).width)
  //   );
  //   const textHeight = wrappedLines.length * lineHeight;

  //   const padding = Math.round(fontSize * 0.4);
  //   const x = img.width - textWidth - padding * 2;
  //   const y = img.height - textHeight - padding * 2;

  //   ctx.fillStyle = "rgba(0,0,0,0.5)";
  //   ctx.fillRect(
  //     x - padding,
  //     y - padding,
  //     textWidth + padding * 2,
  //     textHeight + padding * 2
  //   );

  //   ctx.fillStyle = "#fff";
  //   wrappedLines.forEach((line, i) => {
  //     ctx.fillText(line, x, y + i * lineHeight);
  //   });

  //   return canvas.toDataURL("image/jpeg", 0.92);
  // }

  // async function geoTagImage(imageSrc, lat, lng, meta) {
  //   if (!imageSrc) return;

  //   const ts = new Date().toISOString();
  //   const formatted_address = await getGeocodeData(lat, lng);
  //   const gpsText = `GeoTagged Image${
  //     formatted_address ? `\n${formatted_address}` : ""
  //   }\n${lat}, ${lng}\n${ts}\nLaddar Africa`;

  //   const watermarkedImg = await addWatermark(imageSrc, gpsText);

  //   const exifObj = {
  //     "0th": {
  //       [piexif.ImageIFD.Make]: "Laddar.Africa",
  //       [piexif.ImageIFD.Model]: "Laddar Forms",
  //     },
  //     Exif: {
  //       [piexif.ExifIFD.DateTimeOriginal]: ts,
  //       [piexif.ExifIFD.UserComment]: meta,
  //     },
  //     GPS: {
  //       [piexif.GPSIFD.GPSLatitudeRef]: lat > 0 ? "N" : "S",
  //       [piexif.GPSIFD.GPSLatitude]: [lat * 1000000, 1000000],
  //       [piexif.GPSIFD.GPSLongitudeRef]: lng > 0 ? "E" : "W",
  //       [piexif.GPSIFD.GPSLongitude]: [lng * 1000000, 1000000],
  //     },
  //   };

  //   return piexif.insert(piexif.dump(exifObj), watermarkedImg);
  // }

  async function addWatermark(base64, text) {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = base64;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = img.width;
    canvas.height = img.height;

    ctx.drawImage(img, 0, 0);

    /* SMALLER FONT */
    const fontSize = Math.round(img.width * 0.032); // ~2.5% of width
    const lineHeight = fontSize * 1.25;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.font = `${fontSize}px Arial`;
    ctx.textBaseline = "top";

    /* Limit watermark width */
    const maxWidth = img.width * 0.5;

    const paragraphs = text.split(/\r?\n/);
    const wrappedLines = [];

    for (const para of paragraphs) {
      const words = para.split(" ");
      let line = "";

      for (const word of words) {
        const testLine = line ? `${line} ${word}` : word;
        const width = ctx.measureText(testLine).width;

        if (width > maxWidth) {
          wrappedLines.push(line);
          line = word;
        } else {
          line = testLine;
        }
      }

      if (line) wrappedLines.push(line);
    }

    const textWidths = wrappedLines.map((l) => ctx.measureText(l).width);
    const textWidth = Math.max(...textWidths);
    const textHeight = wrappedLines.length * lineHeight;

    const padding = Math.round(fontSize * 0.4);

    const x = img.width - textWidth - padding * 2;
    const y = img.height - textHeight - padding * 2;

    /* background */
    ctx.fillStyle = "rgba(0,0,0,0.45)";
    ctx.fillRect(
      x - padding,
      y - padding,
      textWidth + padding * 2,
      textHeight + padding * 2,
    );

    /* text */
    ctx.fillStyle = "#fff";

    wrappedLines.forEach((line, i) => {
      ctx.fillText(line, x, y + i * lineHeight);
    });

    return canvas.toDataURL("image/jpeg", 0.92);
  }

  /* -----------------------------
     Add EXIF + GPS metadata
  ----------------------------- */
  async function geoTagImage(imageSrc, lat, lng, meta) {
    if (!imageSrc) return;

    const m = 1000000;
    const xlat = lat * m;
    const xlng = lng * m;

    const ts = new Date();
    const tsFormatted = ts.toLocaleString();

    const gpsText = `GPS: ${lat}, ${lng}
    ${tsFormatted}
    Laddar Africa`;

    const watermarkedImg = await addWatermark(imageSrc, gpsText);

    const zeroth = {};
    zeroth[piexif.ImageIFD.Make] = "Laddar.Africa";
    zeroth[piexif.ImageIFD.Model] = "LaddarOS";
    zeroth[piexif.ImageIFD.Software] = "AgentApp_DataForm";

    const gps = {};
    gps[piexif.GPSIFD.GPSLatitudeRef] = lat > 0 ? "N" : "S";
    gps[piexif.GPSIFD.GPSLatitude] = [xlat, m];
    gps[piexif.GPSIFD.GPSLongitudeRef] = lng > 0 ? "E" : "W";
    gps[piexif.GPSIFD.GPSLongitude] = [xlng, m];

    const exif = {};
    exif[piexif.ExifIFD.DateTimeOriginal] = ts;
    exif[piexif.ExifIFD.UserComment] = meta;

    // Combine EXIF data
    const exifObj = { "0th": zeroth, Exif: exif, GPS: gps };
    const exifStr = piexif.dump(exifObj);

    // Insert EXIF into image
    const newImage = piexif.insert(exifStr, watermarkedImg);
    return newImage;
  }

  const capture = useCallback(
    async (gpsCoords) => {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        setErrorMsg("No photo image found.");
        showFieldError(true);
        return;
      }

      const taggedImgSrc = await geoTagImage(
        imageSrc,
        gpsCoords.latitude.toFixed(6),
        gpsCoords.longitude.toFixed(6),
        "Captured using Laddar Africa",
      );

      setImgSrc(taggedImgSrc);
      setCapturedData(JSON.stringify({ image: taggedImgSrc, gps: gpsCoords }));
      setOpenWebCamDialog(false);
    },
    [webcamRef],
  );

  const { coords } = useGeolocated({
    positionOptions: { enableHighAccuracy: true },
    userDecisionTimeout: 5000,
  });

  function getGPSCoords() {
    if (!coords) return false;
    return { latitude: coords.latitude, longitude: coords.longitude };
  }

  return (
    <Box>
      {/* ERROR */}
      <Snackbar open={fieldError} autoHideDuration={5000}>
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>

      {/* CAMERA DIALOG */}
      <Dialog open={openWebCamDialog}>
        <DialogTitle>Take a picture</DialogTitle>
        <DialogContent>
          <Webcam
            width="100%"
            ref={webcamRef}
            screenshotQuality={1}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            mirrored={cameraUsed}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenWebCamDialog(false)}>
            <Icon>close</Icon>
          </Button>
          <Button onClick={() => setCameraUsed(!cameraUsed)}>
            <Icon>cameraswitch</Icon>
          </Button>
          <Button
            onClick={() => {
              const gps = getGPSCoords();
              if (!gps) {
                setErrorMsg(
                  "GPS coords not captured. Try enabling location access.",
                );
                showFieldError(true);
                return;
              }
              capture(gps);
            }}
          >
            <Icon>photo_camera</Icon>
          </Button>
        </DialogActions>
      </Dialog>

      {/* HEADER */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="h6" fontSize="small">
            {displayName}
          </Typography>
        </Grid>
        <Grid item xs={6} textAlign="right">
          <IconButton onClick={() => setOpenWebCamDialog(true)}>
            <Icon>camera_alt</Icon>
          </IconButton>
        </Grid>
      </Grid>

      {/* ✅ CAPTURE BOX */}
      <Box
        sx={{
          border: "2px dashed #d2d6da",
          borderRadius: "12px",
          height: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#f8f9fa",
          mt: 2,
        }}
      >
        {!imgSrc && (
          <Box textAlign="center">
            <IconButton
              color="primary"
              onClick={() => setOpenWebCamDialog(true)}
              sx={{
                width: 64,
                height: 64,
                backgroundColor: "#e3f2fd",
              }}
            >
              <Icon fontSize="large">photo_camera</Icon>
            </IconButton>

            <Typography variant="caption" mt={1} display="block">
              Tap to take photo
            </Typography>
          </Box>
        )}

        {imgSrc && (
          <>
            <img
              src={imgSrc}
              alt="captured"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />

            <IconButton
              onClick={() => {
                setImgSrc(null);
                setCapturedData(null);
              }}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                backgroundColor: "white",
              }}
            >
              <Icon color="error">close</Icon>
            </IconButton>
          </>
        )}
      </Box>

      <input
        id={`dff_${fieldRef}`}
        type="hidden"
        name={fieldRef}
        value={capturedData}
      />
    </Box>
  );
}

GeoTaggedImageField.propTypes = {
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.any),
};

export default GeoTaggedImageField;
